import { DragEvent, ReactNode, useEffect, useRef, useState } from 'react'
import Konva from 'konva'
import { Pencil, ZoomIn } from 'lucide-react'
import {
  Image as KonvaImage,
  Layer,
  Line,
  Rect,
  Stage,
  Transformer,
} from 'react-konva'
import Card from 'app/frontend/base/components/Card'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  IconKind,
  VisualShape,
  VisualZoom,
} from 'app/frontend/types/custom/VisualPlan'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import { getEncounterCreatures } from 'app/frontend/base/api/encounterCreatures'
import {
  DEFAULT_ICON_OPACITY,
  DEFAULT_ICON_STROKE_COLOR,
  DEFAULT_ICON_STROKE_WIDTH,
  DEFAULT_SHAPE_STYLE,
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
  DEFAULT_ZOOM_SCALE,
  FALLBACK_ASPECT,
  MIN_SIZE,
  DEFAULT_ICON_STYLE,
  IconStyle,
  ShapeStyle,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  zoomStageAttrs,
} from './constants'
import { iconKindOrder } from './iconKinds'
import {
  ENCOUNTER_GATEWAY_ITEM,
  setActiveEncounterCreatures,
} from './encounterCreatureIcons'
import { getEncounterAbilityIconItems } from './encounterAbilityIcons'
import AbilityWowheadOverlay from './panels/AbilityWowheadOverlay'
import { createIcon, createShape, createText } from './shapes/shapeFactory'
import { nextIconName, nextShapeName } from './shapes/shapeLabels'
import { getEntityNumberStarts } from './shapes/entityNumbering'
import { isFramedIconShape, isIconShape } from './shapes/iconShapes'
import ShapeRenderer from './shapes/ShapeRenderer'
import { ShapeInteraction } from './shapes/shapeInteraction'
import ShapeToolbar, { Tool } from './panels/ShapeToolbar'
import IconsToolbar from './panels/IconsToolbar'
import ShapePropertiesToolbar from './panels/ShapePropertiesToolbar'
import ShapeList from './panels/ShapeList'
import TextEditorOverlay from './panels/TextEditorOverlay'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useStickyTop } from 'app/frontend/base/hooks/useStickyTop'
import { useEncounterImage } from './hooks/useEncounterImage'
import { useStageCursor } from './hooks/useStageCursor'
import { useShapeKeyboard } from './hooks/useShapeKeyboard'
import { useSelectionBox } from './hooks/useSelectionBox'
import { useShapeDragging } from './hooks/useShapeDragging'
import { useShapeTransformer } from './hooks/useShapeTransformer'
import { usePencilDrawing } from './hooks/usePencilDrawing'
import {
  contentPointUnderCursor,
  useCanvasZoomPan,
} from './hooks/useCanvasZoomPan'
import { useViewZoom } from './hooks/useViewZoom'
import ZoomOverviewThumbnail from './ZoomOverviewThumbnail'
import { hasSidebarDragData, parseSidebarDragData } from './sidebarDrag'

type Props = {
  screenId: string
  encounterId: number | undefined
  difficulty: Difficulty
  imageIndex: number
  shapes: VisualShape[]
  canEdit: boolean
  selectedIds: string[]
  onSelectIds: (ids: string[]) => void
  onChange: (shapes: VisualShape[]) => void
  zoom?: VisualZoom
  onZoomChange: (zoom?: VisualZoom) => void
  zoomMode: boolean
  onZoomModeChange: (zoomMode: boolean) => void
  header?: ReactNode
}

const VisualScreenEditor = ({
  screenId,
  encounterId,
  difficulty,
  imageIndex,
  shapes,
  canEdit,
  selectedIds,
  onSelectIds,
  onChange,
  zoom,
  onZoomChange,
  zoomMode,
  onZoomModeChange,
  header,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<Konva.Stage>(null)
  const nodeRefs = useRef<Map<string, Konva.Node>>(new Map())
  const lastStyleRef = useRef<ShapeStyle>(DEFAULT_SHAPE_STYLE)
  const lastIconStyleRef = useRef<IconStyle>(DEFAULT_ICON_STYLE)
  const [width, setWidth] = useState(0)
  const [sidebarTab, setSidebarTab] = useState<'add' | 'list'>('add')
  const stickyTop = useStickyTop()

  const image = useEncounterImage(encounterId, imageIndex)
  const aspect = image ? image.height / image.width : FALLBACK_ASPECT
  const height = width * aspect
  const [viewZoom, setViewZoom] = useViewZoom(zoom)
  const activeZoom = canEdit ? zoom : viewZoom
  const handleViewZoomChange = canEdit ? onZoomChange : setViewZoom

  const { data: encounterCreatures = [] } = useQuery({
    queryKey: ['encounterCreatures', encounterId],
    queryFn: () => {
      if (encounterId === undefined) return Promise.resolve([])
      return getEncounterCreatures(encounterId)
    },
    enabled: encounterId !== undefined,
  })

  setActiveEncounterCreatures(encounterId, encounterCreatures)

  const encounterItems = [
    ...encounterCreatures.map((creature) => ({
      value: String(creature.id),
      label: creature.name,
    })),
    ENCOUNTER_GATEWAY_ITEM,
  ]

  const abilityItems = getEncounterAbilityIconItems(encounterId, difficulty)

  const { setCursor } = useStageCursor(stageRef)

  const [tool, setTool] = useState<Tool>('select')
  const [editingTextId, setEditingTextId] = useState<string | null>(null)
  const textEditOriginRef = useRef<string | null>(null)

  useEffect(() => {
    setTool('select')
    setEditingTextId(null)
    textEditOriginRef.current = null
    nodeRefs.current.clear()
  }, [screenId])

  useEffect(() => {
    if (!zoomMode) return
    onSelectIds([])
    setTool('select')
    if (!zoom || zoom.scale <= 1) {
      onZoomChange({ scale: DEFAULT_ZOOM_SCALE, x: 0.5, y: 0.5 })
    }
  }, [zoomMode])

  const lockedIds = new Set(shapes.filter((s) => s.locked).map((s) => s.id))
  const hasLockedSelected = selectedIds.some((id) => lockedIds.has(id))
  const entityNumberStarts = getEntityNumberStarts(shapes)

  const {
    transformerRef,
    handleTransformStart,
    handleTransform,
    handleTransformEnd,
    isResizing,
  } = useShapeTransformer({
    nodeRefs,
    selectedIds,
    shapes,
    width,
    height,
    canEdit,
    tool,
    onChange,
  })

  const {
    selectionRect,
    onMouseDown: onSelectionMouseDown,
    onMouseMove: onSelectionMouseMove,
    onMouseUp: onSelectionMouseUp,
  } = useSelectionBox({
    stageRef,
    nodeRefs,
    canEdit,
    onSelectIds: onSelectIds,
    canSelect: (id) => !lockedIds.has(id),
  })

  const removeShapes = (ids: string[]) => {
    ids.forEach((id) => nodeRefs.current.delete(id))
    onSelectIds([])
    onChange(shapes.filter((s) => !ids.includes(s.id)))
  }

  const {
    draftPoints,
    isActive: isDrawing,
    startDrawing,
    draw,
    endDrawing,
  } = usePencilDrawing({
    stageRef,
    width,
    height,
    shapes,
    onChange,
    onSelectIds: onSelectIds,
    getStyle: () => lastStyleRef.current,
  })

  const { onPanStart, onPanMove, onPanEnd, isPanning, onWheelZoom } =
    useCanvasZoomPan({
      width,
      height,
      zoom: activeZoom,
      onZoomChange: handleViewZoomChange,
    })

  const handleToolChange = (next: Tool) => {
    if (zoomMode) onZoomModeChange(false)
    setTool(next)
    if (next === 'pencil') onSelectIds([])
  }

  useEffect(() => {
    if (zoomMode) {
      setCursor('grab')
      return
    }
    setCursor(tool === 'pencil' ? 'crosshair' : 'default')
  }, [tool, zoomMode])

  useEffect(() => {
    if (tool !== 'pencil') return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleToolChange('select')
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [tool])

  useEffect(() => {
    if (!zoomMode) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finishZoomMode()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [zoomMode, zoom])

  useShapeKeyboard({
    canEdit,
    selectedIds,
    shapes,
    width,
    height,
    onChange,
    onSelectIds: onSelectIds,
    onRemove: removeShapes,
  })

  const {
    isInsideTransformerBounds,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    startStageDrag,
    handleStageDragMove,
    handleStageDragEnd,
    isStageDragging,
  } = useShapeDragging({
    stageRef,
    transformerRef,
    nodeRefs,
    selectedIds,
    shapes,
    width,
    height,
    onChange,
    setCursor,
  })

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const selectedShape =
    selectedIds.length === 1
      ? shapes.find((s) => s.id === selectedIds[0])
      : undefined

  const selectedShapes = shapes.filter((s) => selectedIds.includes(s.id))
  const keepRatioShapes =
    selectedShapes.length > 0 && selectedShapes.every((s) => isIconShape(s))
  const widthOnlyShapes =
    selectedShapes.length > 0 &&
    selectedShapes.every(
      (s) => s.type === 'line' || s.type === 'arrow' || s.type === 'text',
    )
  const editingTextShape =
    editingTextId !== null
      ? shapes.find(
          (s): s is Extract<VisualShape, { type: 'text' }> =>
            s.id === editingTextId && s.type === 'text',
        )
      : undefined

  useEffect(() => {
    if (!selectedShape) return
    if (isIconShape(selectedShape)) {
      const zoomScale = zoom && zoom.scale > 1 ? zoom.scale : 1
      lastIconStyleRef.current = {
        size: selectedShape.width * zoomScale,
        opacity: selectedShape.opacity ?? DEFAULT_ICON_OPACITY,
        entity_count: selectedShape.entity_count ?? 0,
        ...(isFramedIconShape(selectedShape)
          ? {
              stroke_rgb: selectedShape.stroke_rgb ?? DEFAULT_ICON_STROKE_COLOR,
              stroke_width:
                (selectedShape.stroke_width ?? DEFAULT_ICON_STROKE_WIDTH) *
                zoomScale,
            }
          : {}),
      }
      return
    }
    if (selectedShape.type === 'text') return
    lastStyleRef.current = {
      fill_rgb: selectedShape.fill_rgb,
      stroke_rgb: selectedShape.stroke_rgb,
      stroke_width: selectedShape.stroke_width,
    }
  }, [selectedShape, zoom])

  const registerNode = (id: string) => (node: Konva.Node | null) => {
    if (node) nodeRefs.current.set(id, node)
    else nodeRefs.current.delete(id)
  }

  const addShape = (
    type: Exclude<VisualShape['type'], 'icon' | 'text'>,
    position?: { x: number; y: number },
  ) => {
    if (zoomMode) onZoomModeChange(false)
    const shape = {
      ...createShape(
        type,
        { width, height, aspect },
        lastStyleRef.current,
        zoom,
        position,
      ),
      name: nextShapeName(shapes, type),
    }
    setTool('select')
    setEditingTextId(null)
    onChange([...shapes, shape])
    onSelectIds([shape.id])
  }

  const startTextEdit = (shapeId: string) => {
    const shape = shapes.find((entry) => entry.id === shapeId)
    if (shape?.type !== 'text') return
    textEditOriginRef.current = shape.text
    setEditingTextId(shapeId)
  }

  const addText = (position?: { x: number; y: number }) => {
    if (zoomMode) onZoomModeChange(false)
    const shape = {
      ...createText(zoom, position),
      name: nextShapeName(shapes, 'text'),
    }
    setTool('select')
    onChange([...shapes, shape])
    onSelectIds([shape.id])
    textEditOriginRef.current = shape.text
    setEditingTextId(shape.id)
  }

  const commitTextEdit = (text: string) => {
    const id = editingTextId
    setEditingTextId(null)
    textEditOriginRef.current = null
    if (!id) return
    const trimmed = text.trim()
    if (!trimmed) {
      removeShapes([id])
      return
    }
    onChange(
      shapes.map((s) =>
        s.id === id && s.type === 'text' ? { ...s, text: trimmed } : s,
      ),
    )
  }

  const cancelTextEdit = () => {
    const id = editingTextId
    const origin = textEditOriginRef.current
    setEditingTextId(null)
    textEditOriginRef.current = null
    if (!id) return
    if (origin !== null && !origin.trim()) {
      removeShapes([id])
      return
    }
    if (origin !== null) {
      onChange(
        shapes.map((s) =>
          s.id === id && s.type === 'text' ? { ...s, text: origin } : s,
        ),
      )
    }
  }

  const addIcon = (
    kind: IconKind,
    value: string,
    position?: { x: number; y: number },
  ) => {
    if (zoomMode) onZoomModeChange(false)
    const shape = {
      ...createIcon(
        kind,
        value,
        { width, height, aspect },
        lastIconStyleRef.current,
        zoom,
        position,
      ),
      name: nextIconName(shapes, kind, value),
    }
    setTool('select')
    onChange([...shapes, shape])
    onSelectIds([shape.id])
  }

  const dropPositionFromEvent = (event: DragEvent) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect || width === 0 || height === 0) return undefined
    return contentPointUnderCursor(activeZoom, {
      x: (event.clientX - rect.left) / width,
      y: (event.clientY - rect.top) / height,
    })
  }

  const handleCanvasDragOver = (event: DragEvent) => {
    if (!canEdit || !hasSidebarDragData(event.dataTransfer)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }

  const handleCanvasDrop = (event: DragEvent) => {
    if (!canEdit) return
    const payload = parseSidebarDragData(event.dataTransfer)
    if (!payload) return
    event.preventDefault()
    const position = dropPositionFromEvent(event)
    if (payload.kind === 'shape') {
      addShape(payload.type, position)
      return
    }
    if (payload.kind === 'text') {
      addText(position)
      return
    }
    addIcon(payload.iconKind, payload.value, position)
  }

  const finishZoomMode = () => {
    if (!zoom || zoom.scale <= 1) onZoomChange(undefined)
    onZoomModeChange(false)
  }

  const buildInteraction = (
    shape: VisualShape,
  ): ShapeInteraction | undefined => {
    if (tool === 'pencil' || zoomMode) return undefined
    if (editingTextId) {
      const noop = () => undefined
      return {
        nodeRef: registerNode(shape.id),
        draggable: false,
        onMouseEnter: noop,
        onMouseLeave: noop,
        onMouseDown: noop,
        onTap: noop,
        onDragStart: noop,
        onDragMove: noop,
        onDragEnd: noop,
      }
    }
    return {
      nodeRef: registerNode(shape.id),
      draggable: canEdit && !shape.locked,
      onMouseEnter: () => setCursor(canEdit ? 'grab' : 'pointer'),
      onMouseLeave: () => setCursor('default'),
      onMouseDown: () =>
        canEdit && !selectedIds.includes(shape.id) && onSelectIds([shape.id]),
      onTap: () => canEdit && onSelectIds([shape.id]),
      onDblClick:
        canEdit && shape.type === 'text'
          ? () => {
              onSelectIds([shape.id])
              startTextEdit(shape.id)
            }
          : undefined,
      onDragStart: handleDragStart(shape),
      onDragMove: handleDragMove(shape),
      onDragEnd: handleDragEnd(shape),
    }
  }

  return (
    <div className='flex flex-col gap-3 sm:flex-row'>
      {canEdit ? (
        <div className='relative shrink-0 sm:w-52'>
          <Card className='py-2 pl-4 pr-0.5 sm:absolute sm:inset-0'>
            <ScrollArea className='h-full [&>[data-radix-scroll-area-viewport]>div]:block!'>
              <div className='pr-4'>
                <Tabs
                  value={sidebarTab}
                  onValueChange={(v) => setSidebarTab(v as 'add' | 'list')}
                >
                  <TabsList className='w-full'>
                    <TabsTrigger value='add' className='flex-1'>
                      Add
                    </TabsTrigger>
                    <TabsTrigger value='list' className='flex-1'>
                      List
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value='add' className='mt-2'>
                    <div className='flex flex-col gap-4'>
                      <ShapeToolbar
                        tool={tool}
                        onToolChange={handleToolChange}
                        onAdd={addShape}
                        onAddText={addText}
                      />
                      {iconKindOrder.map((kind) => (
                        <IconsToolbar
                          key={kind}
                          kind={kind}
                          items={
                            kind === 'encounter'
                              ? encounterItems
                              : kind === 'ability'
                              ? abilityItems
                              : undefined
                          }
                          onAdd={addIcon}
                        />
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value='list' className='mt-2'>
                    <ShapeList
                      shapes={shapes}
                      canEdit={canEdit}
                      aspect={aspect}
                      selectedIds={selectedIds}
                      onSelectIds={onSelectIds}
                      onChange={onChange}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollArea>
          </Card>
        </div>
      ) : null}

      <Card
        className={cn(
          'min-w-0 flex-1 p-0',
          (tool === 'pencil' || zoomMode) && 'ring-2 ring-primary',
        )}
      >
        {header ? <div className='p-2'>{header}</div> : null}
        <div className='relative'>
          <div className={cn('pointer-events-none sticky z-10 h-0', stickyTop)}>
            {canEdit &&
            !zoomMode &&
            tool !== 'pencil' &&
            !editingTextId &&
            selectedIds.length > 0 ? (
              <div className='absolute inset-x-0 top-0 p-1'>
                <ShapePropertiesToolbar
                  selectedShapes={selectedShapes}
                  onUpdate={(updater) =>
                    onChange(
                      shapes.map((s) =>
                        selectedIds.includes(s.id) ? updater(s) : s,
                      ),
                    )
                  }
                  onRemove={() => removeShapes(selectedIds)}
                  onEditText={
                    selectedShape?.type === 'text'
                      ? () => startTextEdit(selectedShape.id)
                      : undefined
                  }
                />
              </div>
            ) : null}
          </div>
          <div
            ref={containerRef}
            className='relative w-full overflow-hidden rounded-b-md'
            style={{ aspectRatio: `1 / ${aspect}` }}
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
          >
            {editingTextShape ? (
              <TextEditorOverlay
                shape={editingTextShape}
                canvasWidth={width}
                canvasHeight={height}
                zoom={activeZoom}
                onCommit={commitTextEdit}
                onCancel={cancelTextEdit}
              />
            ) : null}
            {!canEdit && width > 0 ? (
              <AbilityWowheadOverlay
                shapes={shapes}
                canvasWidth={width}
                canvasHeight={height}
                zoom={activeZoom}
              />
            ) : null}
            {tool === 'pencil' && !zoomMode ? (
              <div className='pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center p-2'>
                <div className='pointer-events-auto flex items-center gap-2 rounded-full border border-border bg-card/95 px-3 py-1 text-xs font-medium shadow'>
                  <Pencil className='h-3.5 w-3.5' />
                  Drawing mode
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='h-6 gap-1 px-2 text-xs'
                    onClick={() => handleToolChange('select')}
                  >
                    Stop
                  </Button>
                </div>
              </div>
            ) : null}
            {zoomMode ? (
              <div className='pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center p-2'>
                <div className='pointer-events-auto flex items-center gap-3 rounded-full border border-border bg-card/95 px-3 py-1 text-xs font-medium shadow'>
                  <ZoomIn className='h-3.5 w-3.5 shrink-0' />
                  Zoom
                  <Slider
                    className='w-32'
                    min={ZOOM_MIN}
                    max={ZOOM_MAX}
                    step={ZOOM_STEP}
                    value={[zoom?.scale ?? DEFAULT_ZOOM_SCALE]}
                    onValueChange={([scale]) =>
                      onZoomChange({
                        scale,
                        x: zoom?.x ?? 0.5,
                        y: zoom?.y ?? 0.5,
                      })
                    }
                  />
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='h-6 gap-1 px-2 text-xs'
                    onClick={finishZoomMode}
                  >
                    Done
                  </Button>
                </div>
              </div>
            ) : null}
            {activeZoom && activeZoom.scale > 1 ? (
              <ZoomOverviewThumbnail
                image={image}
                aspect={aspect}
                zoom={activeZoom}
                onZoomChange={handleViewZoomChange}
              />
            ) : null}
            {width > 0 ? (
              <Stage
                ref={stageRef}
                width={width}
                height={height}
                {...zoomStageAttrs(activeZoom, width, height)}
                onMouseDown={(event) => {
                  if (zoomMode) {
                    onPanStart(event.evt.clientX, event.evt.clientY)
                    setCursor('grabbing')
                    return
                  }
                  if (canEdit && tool === 'pencil') {
                    startDrawing()
                    return
                  }
                  if (event.target !== event.target.getStage()) return
                  if (
                    selectedIds.length > 0 &&
                    !hasLockedSelected &&
                    isInsideTransformerBounds()
                  ) {
                    startStageDrag()
                    return
                  }
                  onSelectIds([])
                  onSelectionMouseDown(event)
                }}
                onMouseMove={(event) => {
                  if (zoomMode) {
                    if (isPanning()) {
                      onPanMove(event.evt.clientX, event.evt.clientY)
                    }
                    return
                  }
                  if (isDrawing()) {
                    draw()
                    return
                  }
                  if (isStageDragging()) {
                    handleStageDragMove()
                    return
                  }
                  if (
                    canEdit &&
                    tool === 'select' &&
                    event.target === event.target.getStage()
                  ) {
                    if (selectedIds.length > 0 && isInsideTransformerBounds()) {
                      setCursor('grab')
                    } else {
                      setCursor('default')
                    }
                  }
                  onSelectionMouseMove(event)
                }}
                onMouseUp={() => {
                  if (zoomMode) {
                    onPanEnd()
                    setCursor('grab')
                    return
                  }
                  if (isDrawing()) {
                    endDrawing()
                    return
                  }
                  if (isStageDragging()) {
                    handleStageDragEnd()
                  } else {
                    onSelectionMouseUp()
                  }
                }}
                onWheel={(event) => {
                  if (!zoomMode) return
                  const rect = containerRef.current?.getBoundingClientRect()
                  if (!rect) return
                  event.evt.preventDefault()
                  onWheelZoom(
                    event.evt.clientX - rect.left,
                    event.evt.clientY - rect.top,
                    event.evt.deltaY,
                  )
                }}
              >
                <Layer>
                  {image ? (
                    <KonvaImage
                      image={image}
                      width={width}
                      height={height}
                      listening={false}
                    />
                  ) : null}
                  {shapes.map((shape) => (
                    <ShapeRenderer
                      key={shape.id}
                      shape={shape}
                      width={width}
                      height={height}
                      interaction={buildInteraction(shape)}
                      showEntityMarkers={
                        !isResizing || !selectedIds.includes(shape.id)
                      }
                      zoomScale={
                        activeZoom && activeZoom.scale > 1
                          ? activeZoom.scale
                          : 1
                      }
                      entityStartNumber={entityNumberStarts.get(shape.id) ?? 1}
                      isEditingText={editingTextId === shape.id}
                      encounterId={encounterId}
                    />
                  ))}
                  {draftPoints.length > 0 ? (
                    <Line
                      points={draftPoints}
                      stroke={
                        lastStyleRef.current.stroke_rgb ?? DEFAULT_STROKE_COLOR
                      }
                      strokeWidth={
                        lastStyleRef.current.stroke_width ??
                        DEFAULT_STROKE_WIDTH
                      }
                      strokeScaleEnabled={false}
                      lineCap='round'
                      lineJoin='round'
                      listening={false}
                    />
                  ) : null}
                  {canEdit && !zoomMode && !editingTextId ? (
                    <Transformer
                      ref={transformerRef}
                      rotateEnabled={!hasLockedSelected}
                      resizeEnabled={!hasLockedSelected}
                      keepRatio={keepRatioShapes}
                      enabledAnchors={
                        keepRatioShapes
                          ? [
                              'top-left',
                              'top-right',
                              'bottom-left',
                              'bottom-right',
                            ]
                          : widthOnlyShapes
                          ? ['middle-left', 'middle-right']
                          : undefined
                      }
                      onTransformStart={handleTransformStart}
                      onTransform={handleTransform}
                      onTransformEnd={handleTransformEnd}
                      boundBoxFunc={(oldBox, newBox) =>
                        newBox.width < MIN_SIZE && newBox.height < MIN_SIZE
                          ? oldBox
                          : newBox
                      }
                    />
                  ) : null}
                  {selectionRect ? (
                    <Rect
                      x={selectionRect.x}
                      y={selectionRect.y}
                      width={selectionRect.width}
                      height={selectionRect.height}
                      fill='rgba(100, 150, 255, 0.15)'
                      stroke='rgba(100, 150, 255, 0.8)'
                      strokeWidth={1}
                      listening={false}
                    />
                  ) : null}
                </Layer>
              </Stage>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  )
}

export default VisualScreenEditor
